/**
 * trackEvent — fire-and-forget analytics emitter.
 *
 * Writes to public.analytics_events via the fn_log_event RPC. The RPC
 * silently no-ops for users with profiles.is_test_account = true so
 * founder/test traffic stays out of investor-grade dashboards.
 *
 * Always swallow errors — analytics must never break the UX.
 */
import { supabase } from "@/integrations/supabase/client";

export type AppId =
  | "fryqnc"
  | "pulsynq"
  | "mindlynq"
  | "ablelynq"
  | "deafwav"
  | "fullynq"
  | "jadesynq"
  | "openlynq"
  | "spiritsynq";

export type AnalyticsKind =
  // session
  | "session_start"
  // discovery / matching
  | "swipe_yes" | "swipe_no" | "match_created" | "daily_signal_accept"
  | "daily_signal_view" | "daily_signal_refresh" | "daily_signal_skip"
  | "discovery_filter_apply" | "discovery_filter_reset"
  // engagement
  | "super_signal_sent" | "endorsement_given" | "milestone_unlocked"
  | "streak_increment"
  // messaging
  | "message_sent" | "voice_note_sent" | "raela_message_sent"
  // safety
  | "report_filed" | "block_created" | "raela_intervention"
  | "low_sensory_mode_on" | "crisis_resources_view" | "emergency_button_press"
  // billing
  | "subscription_paid" | "alacarte_paid" | "trial_started" | "trial_converted"
  // identity / kyc
  | "kyc_started" | "kyc_completed"
  // events
  | "event_rsvp" | "event_checkin"
  // mentor bookings
  | "mentor_booking_request" | "mentor_booking_confirm"
  | "mentor_booking_decline" | "mentor_booking_cancel"
  // raela
  | "raela_detection"
  // funnel
  | "cta_get_started" | "welcome_create_account" | "auth_page_view"
  | "auth_signup_complete" | "auth_signin_complete";

export interface TrackOpts {
  app?: AppId | null;
  props?: Record<string, unknown>;
}

export async function trackEvent(kind: AnalyticsKind, opts: TrackOpts = {}) {
  try {
    await supabase.rpc("fn_log_event" as never, {
      _kind: kind,
      _app: opts.app ?? null,
      _props: (opts.props ?? {}) as never,
    } as never);
  } catch (err) {
    // Never block UX. Surface in dev only.
    if (import.meta.env.DEV) console.warn("[analytics] track failed", kind, err);
  }
}

/** One-shot session start — call once per app load after auth resolves. */
export function trackSessionStart(app?: AppId | null) {
  return trackEvent("session_start", { app });
}
